"use client";

import { useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  Alert,
  Button,
  Dialog,
  DialogBackdrop,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@/ui";
import useMutateRecruiter from "@lib/user/useMutateRecruiter";
import useMutateRecruiterSettings from "@lib/user/useMutateRecruiterSettings";
import useRecruiterDetails from "@lib/user/useRecruiterDetails";
import useRecruiterSettings from "@lib/user/useRecruiterSettings";
import Recruiter from "./parts/Recruiter";
import Theme from "./parts/Theme";
import FloatsSubmissions from "./parts/FloatsSubmissions";
import Ai from "./parts/Ai";
import validateRecruiterUrls from "@lib/helpers/validateRecruiterUrls";
import styles from "./Settings.module.css";

const COMPANY_DESCRIPTION_EMAIL =
  process.env.NEXT_PUBLIC_COMPANY_DESCRIPTION_EMAIL || "matthew@vandc.uk";

const RECRUITER_TAB = "recruiter";

const TABS = [
  { value: RECRUITER_TAB, label: "Recruiter" },
  { value: "theme", label: "Theme" },
  { value: "outreach", label: "Outreach" },
  { value: "ai", label: "AI" },
];

/**
 * What the next arrival of fetched data should do to the local form state.
 *
 * - `full`      — normal case: adopt the server's values.
 * - `filesOnly` — an immediate file upload just landed; adopt only the derived
 *                 URL/path fields so in-progress edits elsewhere survive.
 * - `skip`      — a save partially failed; the succeeded half's refetch must not
 *                 reset the form while the user is fixing the errors.
 *
 * Each non-`full` mode is consumed by exactly one sync pass, then resets. That
 * is safe only because every write invalidates exactly one query key, so only
 * one refetch ever settles per action.
 */
type SyncMode = "full" | "filesOnly" | "skip";

const EMPTY_SETTINGS: Record<string, unknown> = {
  id: "",
  company: "",
  reply_to_email: "",
  calendar_url: "",
  logo_url: "",
  job_title: "",
  company_description: "",
  photo_url: "",
  banner_image_url: "",
  primary_color: "",
  secondary_color: "",
  type: "",
  job_card_type: "",
  email_signature_block: "",
  mobile_phone: "",
  linkedin_url: "",
  website: "",
  instagram: "",
  float_header_items: [],
  reviews_widget: "",
  show_bubbles: false,
  hide_recruiter_name: false,
  use_small_photo: false,
  show_not_interested_button: false,
  user_base_prompt: "",
  cv_import_model: "fast",
  terms_document_path: "",
  terms_document_filename: "",
  float_terms_acceptance: false,
  dashboard_focus: "floats",
  disable_skill_emojis: false,
  float_initial_skills: 6,
  float_initial_roles: 3,
  float_initial_projects: 2,
  float_initial_education: 3,
  submission_initial_skills: 6,
  submission_initial_roles: 3,
  submission_initial_projects: 2,
  submission_initial_education: 3,
  float_details_heading: "",
  float_portfolio_heading: "",
  float_skills_heading: "",
  submission_details_heading: "",
  submission_portfolio_heading: "",
  submission_skills_heading: "",
};

/** Pull the validation errors out of a settled mutation, if it rejected. */
function errorsFrom(
  result: PromiseSettledResult<unknown>,
): Record<string, string> {
  if (result.status === "fulfilled") {
    return {};
  }

  return (
    (
      result.reason as {
        response?: { data?: { errors?: Record<string, string> } };
      }
    )?.response?.data?.errors ?? {}
  );
}

export default function Settings({
  onClose,
  open,
}: {
  onClose: () => void;
  open: boolean;
}) {
  const [tab, setTab] = useState(RECRUITER_TAB);
  const [syncMode, setSyncMode] = useState<SyncMode>("full");
  const [formError, setFormError] = useState<Record<string, unknown>>({});
  const [recruiter, setRecruiter] = useState(EMPTY_SETTINGS);
  const errorAlertRef = useRef<HTMLDivElement>(null);
  const { recruiter: recruiterData, isLoading: isRecruiterLoading } =
    useRecruiterDetails();
  const { settings: settingsData, isLoading: isSettingsLoading } =
    useRecruiterSettings();
  const { mutation } = useMutateRecruiter();
  const { mutation: settingsMutation } = useMutateRecruiterSettings();

  const isLoading = isRecruiterLoading || isSettingsLoading;

  // The dialog edits one flat object spanning both endpoints, so the two
  // payloads are merged here and split apart again at save time. Their key sets
  // are disjoint by construction (see Settings.test.tsx), so neither can shadow
  // the other. `id` is applied last and is always the recruiter UUID.
  const fetched = useMemo(
    () =>
      recruiterData && settingsData
        ? { ...recruiterData, ...settingsData, id: recruiterData.uuid }
        : null,
    [recruiterData, settingsData],
  );

  // Sync freshly fetched data into the editable local state whenever the fetched
  // payload changes. Uses React's "adjust state during render" pattern with the
  // previous payload tracked in state (not a ref), so it doesn't cause a
  // cascading re-render.
  const fetchedKey = !isLoading && fetched ? JSON.stringify(fetched) : null;
  const [prevFetchedKey, setPrevFetchedKey] = useState<string | null>(null);
  if (fetchedKey && fetched && fetchedKey !== prevFetchedKey) {
    setPrevFetchedKey(fetchedKey);

    if (syncMode === "skip") {
      // A save partially failed; keep what the user has on screen.
      setSyncMode("full");
    } else if (syncMode === "filesOnly") {
      // Only a file was uploaded, so don't overwrite the other fields.
      setRecruiter((current) => ({
        ...current,
        logo_url: fetched.logo_url || "",
        photo_url: fetched.photo_url || "",
        banner_image_url: fetched.banner_image_url || "",
        terms_document_path: fetched.terms_document_path || "",
        terms_document_filename: fetched.terms_document_filename || "",
      }));
      setSyncMode("full");
    } else {
      setRecruiter(fetched);
    }
  }

  const scrollToErrors = () => {
    requestAnimationFrame(() => {
      errorAlertRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleSave = async () => {
    // Block save on invalid URL fields and surface the errors.
    const urlErrors = validateRecruiterUrls(recruiter);
    if (Object.keys(urlErrors).length > 0) {
      setTab(RECRUITER_TAB);
      setFormError(urlErrors);
      scrollToErrors();
      return;
    }

    // Split by the server's own key set rather than a hardcoded list, so the
    // two payloads can never drift from what each endpoint accepts.
    const settingsKeys = new Set(Object.keys(settingsData ?? {}));
    const settingsPayload: Record<string, unknown> = {};
    const recruiterPayload: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(recruiter)) {
      if (settingsKeys.has(key)) {
        settingsPayload[key] = value;
      } else {
        recruiterPayload[key] = value;
      }
    }

    // Run both in parallel: sequential would abort the second on the first
    // failure, so the user would only see half their validation errors.
    const results = await Promise.allSettled([
      mutation.mutateAsync(
        recruiterPayload as Record<string, unknown> & { id: string },
      ),
      settingsMutation.mutateAsync(settingsPayload),
    ]);

    const errors = { ...errorsFrom(results[0]), ...errorsFrom(results[1]) };

    if (results.some((result) => result.status === "rejected")) {
      // The half that succeeded has already persisted and will refetch; absorb
      // that one sync so the user keeps the edits they're about to correct.
      setSyncMode("skip");
      setFormError(errors);
      if (Object.keys(errors).length > 0) {
        scrollToErrors();
      }
      return;
    }

    toast.success("Settings saved");
    setFormError({});
    onClose();
  };

  // no recruiter settings loaded
  if (!recruiter.id) {
    return null;
  }

  // Laravel returns either a bare string or an array of messages per field.
  const errorMessages = Object.values(formError).flatMap((value) =>
    Array.isArray(value) ? value : [value],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="xl">
          <DialogHeader>
            <DialogTitle>Settings</DialogTitle>
            <DialogClose aria-label="Close settings" />
          </DialogHeader>
          <Tabs value={tab} onChange={setTab} className={styles.tabs}>
            <TabList className={styles.tabList}>
              {TABS.map(({ value, label }) => (
                <Tab key={value} value={value}>
                  {label}
                </Tab>
              ))}
            </TabList>
            <DialogBody className={styles.body}>
              {errorMessages.length > 0 && (
                <div ref={errorAlertRef} className={styles.errors}>
                  <Alert variant="error">
                    {errorMessages.map((message, index) => (
                      <div key={index}>{String(message)}</div>
                    ))}
                  </Alert>
                </div>
              )}
              <TabPanel value={RECRUITER_TAB} className={styles.panel}>
                <Recruiter
                  recruiter={recruiter}
                  formError={formError}
                  setRecruiter={setRecruiter}
                  setFormError={setFormError}
                  onFileUpload={() => setSyncMode("filesOnly")}
                  mutation={mutation}
                  settingsMutation={settingsMutation}
                  hasOutlookIntegration={
                    recruiterData?.user?.integration_connections?.some(
                      (conn: { type: string }) => conn.type === "outlook",
                    ) || false
                  }
                  showCompanyDescription={
                    recruiterData?.user?.email === COMPANY_DESCRIPTION_EMAIL
                  }
                />
              </TabPanel>
              <TabPanel value="theme" className={styles.panel}>
                <Theme
                  recruiter={recruiter}
                  formError={formError}
                  setRecruiter={setRecruiter}
                  setFormError={setFormError}
                  onFileUpload={() => setSyncMode("filesOnly")}
                  mutation={mutation}
                />
              </TabPanel>
              <TabPanel value="outreach" className={styles.panel}>
                <FloatsSubmissions
                  recruiter={recruiter}
                  setRecruiter={setRecruiter}
                />
              </TabPanel>
              <TabPanel value="ai" className={styles.panel}>
                <Ai
                  recruiter={recruiter}
                  setRecruiter={setRecruiter}
                  onClose={onClose}
                />
              </TabPanel>
            </DialogBody>
          </Tabs>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
